import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Trash2, Edit3 } from 'lucide-react'
import { toast } from 'sonner'

import { blog, type BlogPost, type BlogCategory } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Input, Textarea } from '../components/Input'
import { Dialog, DialogContent, DialogClose } from '../components/Dialog'
import { formatDateTime } from '../lib/format'
import { cn } from '../lib/cn'

type Tab = 'posts' | 'categories'

export function BlogPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('posts')
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  // Category dialog state
  const [editingCat, setEditingCat] = useState<BlogCategory | null>(null)
  const [newCatOpen, setNewCatOpen] = useState(false)
  const [catName, setCatName] = useState('')
  const [catSlug, setCatSlug] = useState('')
  const [catDescription, setCatDescription] = useState('')
  const [catSaving, setCatSaving] = useState(false)

  function reloadPosts() {
    setLoading(true)
    blog
      .listAll()
      .then((r) => setPosts(r.posts))
      .catch((err) => toast.error('Failed to load posts', { description: err.message }))
      .finally(() => setLoading(false))
  }

  function reloadCategories() {
    setCategoriesLoading(true)
    blog
      .listCategories()
      .then((r) => setCategories(r.categories))
      .catch((err) => toast.error('Failed to load categories', { description: err.message }))
      .finally(() => setCategoriesLoading(false))
  }

  useEffect(() => {
    reloadPosts()
    reloadCategories()
  }, [])

  async function togglePublish(p: BlogPost) {
    try {
      await blog.togglePublish(p.id)
      toast.success(p.isPublished ? 'Unpublished' : 'Published')
      reloadPosts()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  async function onDelete(p: BlogPost) {
    if (!confirm(`Delete "${p.title}"? This cannot be undone.`)) return
    try {
      await blog.delete(p.id)
      toast.success('Deleted')
      reloadPosts()
    } catch (err) {
      toast.error('Delete failed', { description: (err as Error).message })
    }
  }

  function openNewCategory() {
    setEditingCat(null)
    setCatName('')
    setCatSlug('')
    setCatDescription('')
    setNewCatOpen(true)
  }

  function openEditCategory(c: BlogCategory) {
    setEditingCat(c)
    setCatName(c.name)
    setCatSlug(c.slug)
    setCatDescription(c.description ?? '')
    setNewCatOpen(true)
  }

  async function saveCategory() {
    if (!catName.trim()) {
      toast.error('Name is required')
      return
    }
    setCatSaving(true)
    try {
      if (editingCat) {
        await blog.updateCategory(editingCat.id, {
          name: catName.trim(),
          slug: catSlug.trim() || undefined,
          description: catDescription.trim() || null,
        })
        toast.success('Category updated')
      } else {
        await blog.createCategory({
          name: catName.trim(),
          slug: catSlug.trim() || undefined,
          description: catDescription.trim() || undefined,
        })
        toast.success('Category created')
      }
      setNewCatOpen(false)
      reloadCategories()
    } catch (err) {
      toast.error('Failed to save category', { description: (err as Error).message })
    } finally {
      setCatSaving(false)
    }
  }

  async function deleteCategory(c: BlogCategory) {
    if (!confirm(`Delete "${c.name}"? Posts in this category will become uncategorized.`))
      return
    try {
      await blog.deleteCategory(c.id)
      toast.success('Deleted')
      reloadCategories()
    } catch (err) {
      toast.error('Delete failed', { description: (err as Error).message })
    }
  }

  const postColumns: ColumnDef<BlogPost>[] = [
    {
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => (
        <Link
          to={`/blog/${row.original.id}`}
          className="text-sm font-medium text-ink hover:text-forest"
        >
          {row.original.title}
        </Link>
      ),
    },
    { header: 'Author', accessorKey: 'authorName' },
    {
      header: 'Category',
      accessorKey: 'categoryId',
      cell: ({ row }) => {
        const cat = categories.find((c) => c.id === row.original.categoryId)
        return <span className="text-xs text-slatey">{cat?.name ?? '—'}</span>
      },
    },
    {
      header: 'Status',
      accessorKey: 'isPublished',
      cell: ({ getValue }) => (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            getValue<boolean>() ? 'bg-forest/10 text-forest' : 'bg-sand text-slatey'
          }`}
        >
          {getValue<boolean>() ? 'Published' : 'Draft'}
        </span>
      ),
    },
    {
      header: 'Published',
      accessorKey: 'publishedAt',
      cell: ({ getValue }) => (
        <span className="text-xs text-slatey">{formatDateTime(String(getValue()))}</span>
      ),
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="ghost" size="sm" onClick={() => togglePublish(row.original)}>
            {row.original.isPublished ? 'Unpublish' : 'Publish'}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(row.original)} title="Delete">
            <Trash2 className="size-4 text-danger" />
          </Button>
        </div>
      ),
    },
  ]

  const categoryColumns: ColumnDef<BlogCategory>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <span className="text-sm font-medium text-ink">{row.original.name}</span>
      ),
    },
    {
      header: 'Slug',
      accessorKey: 'slug',
      cell: ({ row }) => <span className="text-xs text-slatey font-mono">{row.original.slug}</span>,
    },
    {
      header: 'Posts',
      accessorKey: 'postCount',
      cell: ({ getValue }) => (
        <span className="text-xs text-slatey">{getValue<number>() ?? 0}</span>
      ),
    },
    {
      header: 'Description',
      accessorKey: 'description',
      cell: ({ row }) => (
        <span className="text-xs text-slatey line-clamp-1">
          {row.original.description ?? '—'}
        </span>
      ),
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="ghost" size="icon" onClick={() => openEditCategory(row.original)} title="Edit">
            <Edit3 className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => deleteCategory(row.original)}
            title="Delete"
          >
            <Trash2 className="size-4 text-danger" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-end justify-between gap-3 border-b border-line">
        <div className="flex gap-1">
          <TabButton active={tab === 'posts'} onClick={() => setTab('posts')}>
            Posts
            <span className="ml-2 text-xs text-slatey">({posts.length})</span>
          </TabButton>
          <TabButton active={tab === 'categories'} onClick={() => setTab('categories')}>
            Categories
            <span className="ml-2 text-xs text-slatey">({categories.length})</span>
          </TabButton>
        </div>
        {tab === 'posts' ? (
          <Button onClick={() => navigate('/blog/new')} size="sm" className="mb-2">
            <Plus className="size-4" />
            New post
          </Button>
        ) : (
          <Button onClick={openNewCategory} size="sm" className="mb-2">
            <Plus className="size-4" />
            New category
          </Button>
        )}
      </div>

      {tab === 'posts' ? (
        <DataTable
          data={posts}
          columns={postColumns}
          loading={loading}
          onRowClick={(p) => navigate(`/blog/${p.id}`)}
          emptyTitle="No blog posts yet"
          emptyDescription="Write your first article to start populating the blog."
          emptyAction={
            <Button onClick={() => navigate('/blog/new')}>
              <Plus className="size-4" />
              New post
            </Button>
          }
        />
      ) : (
        <DataTable
          data={categories}
          columns={categoryColumns}
          loading={categoriesLoading}
          emptyTitle="No categories yet"
          emptyDescription="Add categories to organize blog posts."
          emptyAction={
            <Button onClick={openNewCategory}>
              <Plus className="size-4" />
              New category
            </Button>
          }
        />
      )}

      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent
          title={editingCat ? 'Edit category' : 'New category'}
          description="Categories help organize blog posts on the public site."
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slatey mb-1.5">
                Name <span className="text-danger">*</span>
              </label>
              <Input
                value={catName}
                onChange={(e) => {
                  setCatName(e.target.value)
                  if (!editingCat && !catSlug) {
                    setCatSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, ''),
                    )
                  }
                }}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slatey mb-1.5">
                Slug
              </label>
              <Input
                value={catSlug}
                onChange={(e) => setCatSlug(e.target.value)}
                placeholder="category-slug"
              />
              <p className="text-xs text-slatey mt-1.5">
                Lowercase letters, digits, hyphens. Auto-generated if blank.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slatey mb-1.5">
                Description
              </label>
              <Textarea
                value={catDescription}
                onChange={(e) => setCatDescription(e.target.value)}
                maxLength={500}
                className="min-h-[80px]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={saveCategory} loading={catSaving}>
                {editingCat ? 'Save changes' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition',
        active
          ? 'border-forest text-ink'
          : 'border-transparent text-slatey hover:text-ink hover:border-line',
      )}
    >
      {children}
    </button>
  )
}